import { inject, Injectable, NgModule } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterModule,
  RouterStateSnapshot,
  Routes,
  TitleStrategy,
  UrlSegment,
} from '@angular/router';
// PAGES
import { DeletedOrdersComponent } from '@app/pages/deleted-orders/deleted-orders/deleted-orders.component';
import { SearchResultsComponent } from '@app/pages/search/components/search-results/search-results.component';
import { AssignComponent } from '@app/pages/workspace/assign/assign.component';
import { AssignTableComponent } from '@app/pages/workspace/assign/components/table/assign-table.component';
import { TrackComponent } from '@app/pages/workspace/track/track.component';
import { PreviewComponent } from '@app/pages/workspace/verify/preview.component';
import { CustomDashboardComponent } from '@pages/custom-dashboard/custom-dashboard.component';
// LAYOUT
import { Title } from '@angular/platform-browser';
import { DefaultLayoutComponent } from '@app/storybook/layouts/default/default-layout.component';
import { CustomDashboardGuard } from '@core/guards/custom-dashboard-guard';
import { SmartCartEnabledGuard } from '@core/guards/smart-cart-enabled.guard';
import { FronteggAuthGuard } from '@frontegg/angular';
import { SettingsPageComponent } from '@pages/settings/settings-page/settings-page.component';
import { EssentialsPlanGuard } from './core/guards/essentials-plan.guard';
import { featureAccessGuard } from './core/guards/feature-access.guard';
import { FeatureKey } from '@pdqdev/pdq-shared/dist/utils/features-manifest';
import { FullPlatformGuard } from './core/guards/full-platform.guard';
import { PendingSplitBillingGuard } from './core/guards/pending-split-billing.guard';
import { MaxxGuard } from './core/guards/maxx.guard';
import { IntegrationsAuthComponent } from './features/integrations-auth/integrations-auth.component';
import { PDQLabsComponent } from './pages/pdq-labs/pdq-labs.component';
import { ReportDownloaderComponent } from './pages/reports/report-downloader/report-downloader.component';
import { FulfillPageComponent } from './pages/workspace/fulfill/fulfill.component';
import { OrderManagementComponent } from './storybook/layouts/components/order-management/order-management.component';
import { WildCardRouteComponent } from './storybook/layouts/components/wild-card-route/wild-card-route.component';
import { ProductPageComponent } from './pages/engage/product-page/product-page.component';
import { SmartCartComponent } from './pages/engage/smart-cart/smart-cart.component';
import { TrackingPageComponent } from './pages/checkout-app/checkout-layout/main/components/tracking-page/tracking-page.component';
import { OrderEditingComponent } from './pages/order-editing/order-editing.component';
import { PromiseAIComponent } from './pages/engage/promise-ai/promise-ai.component';
import { environment } from '@environment/environment';
import { MaintenancePageComponent } from '@app/sharedModule/components/maintenance-page/maintenance-page.component';
import { MaxxComponent } from './pages/maxx/maxx.component';
import { MAXX_DISPLAY_NAME, MAXX_PATH } from './pages/maxx/constants';
import { StrategyCatalogComponent } from './pages/strategy-catalog/strategy-catalog.component';
import { StrategyDetailsComponent } from './pages/strategy-catalog/components/strategy-details/strategy-details.component';
import { ImpactDashboardComponent } from './pages/impact-dashboard/impact-dashboard.component';

@Injectable({ providedIn: 'root' })
export class TemplatePageTitleStrategy extends TitleStrategy {
  constructor(private readonly title: Title) {
    super();
  }
  override updateTitle(routerState: RouterStateSnapshot) {
    const title = this.buildTitle(routerState);
    if (title !== undefined) {
      this.title.setTitle(`${title} | PrettyDamnQuick`);
    }
  }
}

export const routes: Routes = [
  {
    matcher: (elements: UrlSegment[]) => {
      if (environment.ON_MAINTENANCE && elements[0]?.path !== 'maintenance') {
        return { consumed: elements };
      }
      return null;
    },
    redirectTo: 'maintenance',
    pathMatch: 'full',
  },
  {
    matcher: (elements: UrlSegment[]) => {
      if (elements[elements.length - 1]?.path?.startsWith('integrationsauth')) {
        return { consumed: elements };
      }
      return null;
    },

    canActivate: [FronteggAuthGuard],
    component: IntegrationsAuthComponent,
  },
  {
    path: 'maintenance',
    component: MaintenancePageComponent,
  },
  // DEFAULT PATH START
  {
    path: '',
    component: DefaultLayoutComponent,
    canActivate: [FronteggAuthGuard],
    children: [
      {
        path: 'settings',
        canActivate: [FronteggAuthGuard],
        children: [
          {
            path: '',
            component: SettingsPageComponent,
            loadChildren: () => import('./pages/settings/settings.module').then((m) => m.SettingsModule),
          },
        ],
      },
      {
        path: 'engage',
        canActivate: [
          FronteggAuthGuard,
          () => inject(PendingSplitBillingGuard).canActivate(),
          (route: ActivatedRouteSnapshot) => inject(EssentialsPlanGuard).canActivate(route),
        ],
        children: [
          {
            title: 'Product page',
            path: 'product-page/new',
            component: ProductPageComponent,
            canActivate: [featureAccessGuard(FeatureKey.PRODUCT_PAGE)],
          },
          {
            title: 'Product page',
            path: 'product-page/:id',
            component: ProductPageComponent,
            canActivate: [featureAccessGuard(FeatureKey.PRODUCT_PAGE)],
          },
          {
            title: 'Product page',
            path: 'product-page',
            component: ProductPageComponent,
            canActivate: [featureAccessGuard(FeatureKey.PRODUCT_PAGE)],
          },
          {
            title: 'Smart Cart',
            path: 'smart-cart/new',
            component: SmartCartComponent,
            canActivate: [() => inject(SmartCartEnabledGuard).canActivate()],
          },
          {
            title: 'Standalone Widgets',
            path: 'smart-cart/standalone/new',
            component: SmartCartComponent,
            canActivate: [() => inject(SmartCartEnabledGuard).canActivate()],
          },
          {
            title: 'Standalone Widgets',
            path: 'smart-cart/standalone/:id',
            component: SmartCartComponent,
            canActivate: [() => inject(SmartCartEnabledGuard).canActivate()],
          },
          {
            title: 'Standalone Widgets',
            path: 'smart-cart/standalone',
            component: SmartCartComponent,
            canActivate: [() => inject(SmartCartEnabledGuard).canActivate()],
          },
          {
            title: 'Smart Cart',
            path: 'smart-cart/:id',
            component: SmartCartComponent,
            canActivate: [() => inject(SmartCartEnabledGuard).canActivate()],
          },
          {
            title: 'Smart Cart',
            path: 'smart-cart',
            component: SmartCartComponent,
            canActivate: [() => inject(SmartCartEnabledGuard).canActivate()],
          },
          {
            title: 'Promise AI',
            path: 'promise-ai-page',
            component: PromiseAIComponent,
          },
        ],
      },
      {
        path: 'retain',
        canActivate: [FronteggAuthGuard, () => inject(PendingSplitBillingGuard).canActivate()],
        children: [
          {
            title: 'Post Purchase',
            path: 'post-purchase',
            component: OrderEditingComponent,
            canActivate: [featureAccessGuard(FeatureKey.POST_PURCHASE)],
          },
          {
            title: 'One-Click Upsell',
            path: 'one-click-upsell',
            canActivate: [featureAccessGuard(FeatureKey.ONE_CLICK_UPSELL)],
            loadChildren: () =>
              import('./pages/one-click-upsell/one-click-upsell.module').then((m) => m.OneClickUpsellModule),
          },
          {
            title: 'Offer Network',
            path: 'offer-network',
            canActivate: [featureAccessGuard(FeatureKey.POST_PURCHASE)],
            loadChildren: () => import('./pages/offer-network/offer-network.module').then((m) => m.OfferNetworkModule),
          },
          {
            title: 'Tracking page',
            path: 'tracking-page',
            component: TrackingPageComponent,
            canActivate: [featureAccessGuard(FeatureKey.TRACKING_PAGE)],
          },
        ],
      },

      {
        path: 'convert',
        canActivate: [
          FronteggAuthGuard,
          () => inject(PendingSplitBillingGuard).canActivate(),
          (route: ActivatedRouteSnapshot) => inject(EssentialsPlanGuard).canActivate(route),
        ],

        children: [
          {
            path: '',
            loadChildren: () => import('./pages/checkout-app/checkout.app.module').then((m) => m.CheckoutAppModule),
          },
        ],
      },
      {
        path: 'deliver',
        canActivate: [
          () => inject(PendingSplitBillingGuard).canActivate(),
          (route: ActivatedRouteSnapshot) => inject(EssentialsPlanGuard).canActivate(route),
        ],
        children: [
          {
            path: 'deleted-deliveries',
            title: 'Deleted orders',
            canActivate: [FronteggAuthGuard],
            children: [{ path: '', component: DeletedOrdersComponent }],
          },
          {
            path: 'search',
            title: 'Search orders',
            canActivate: [FronteggAuthGuard],
            children: [{ path: '', component: SearchResultsComponent }],
          },
          {
            path: 'manage',
            title: 'Order management',
            component: OrderManagementComponent,
            canActivate: [FronteggAuthGuard, featureAccessGuard(FeatureKey.ORDER_MANAGEMENT)],
            children: [
              {
                path: 'verify',
                component: PreviewComponent,
              },

              {
                path: 'assign',
                canActivate: [() => inject(FullPlatformGuard).canActivate()],
                component: AssignComponent,
                children: [
                  {
                    path: '',
                    component: AssignTableComponent,
                  },
                ],
              },
              {
                path: 'fulfill',
                canActivate: [() => inject(FullPlatformGuard).canActivate()],
                component: FulfillPageComponent,
              },
              {
                path: 'track',
                component: TrackComponent,
              },
              {
                path: '**',
                redirectTo: 'verify',
                pathMatch: 'full',
              },
            ],
          },
        ],
      },
      {
        title: 'Optimize',
        path: 'optimize',
        canActivate: [FronteggAuthGuard, () => inject(PendingSplitBillingGuard).canActivate()],
        children: [
          {
            path: 'impact-dashboard',
            title: 'Impact Dashboard',
            canActivate: [FronteggAuthGuard],
            children: [{ path: '', component: ImpactDashboardComponent }],
          },
          {
            path: 'dashboard',
            title: 'Analytics dashboard',
            canActivate: [FronteggAuthGuard, featureAccessGuard(FeatureKey.ANALYTICS)],
            children: [{ path: '', component: PDQLabsComponent }],
          },
          {
            path: 'custom-dashboard',
            title: 'Custom dashboard',
            canActivate: [() => inject(CustomDashboardGuard).canActivate()],
            children: [{ path: '', component: CustomDashboardComponent }],
          },
          {
            path: 'strategy-catalog',
            title: 'Strategies',
            canActivate: [FronteggAuthGuard, featureAccessGuard(FeatureKey.STRATEGY_CATALOG)],
            children: [
              { path: '', component: StrategyCatalogComponent },
              {
                path: 'roadmap',
                title: 'My Roadmap',
                component: StrategyCatalogComponent,
              },
              {
                path: ':id',
                component: StrategyDetailsComponent,
              },
            ],
          },
          {
            path: MAXX_PATH,
            title: MAXX_DISPLAY_NAME,
            canActivate: [
              FronteggAuthGuard,
              featureAccessGuard(FeatureKey.MAXX),
              () => inject(MaxxGuard).canActivate(),
            ],
            children: [{ path: '', component: MaxxComponent }],
          },
          {
            path: 'ab-tests',
            title: 'A/B tests',
            canActivate: [FronteggAuthGuard, featureAccessGuard(FeatureKey.AB_TESTS)],
            loadChildren: () => import('./pages/checkout-app/checkout.app.module').then((m) => m.CheckoutAppModule),
          },
          {
            path: 'audit',
            title: 'Audit',
            canActivate: [FronteggAuthGuard],
            loadChildren: () => import('./pages/audit/audit.module').then((m) => m.AuditModule),
          },
        ],
      },
      {
        path: 'reports/:id',
        canActivate: [FronteggAuthGuard, () => inject(PendingSplitBillingGuard).canActivate()],
        component: ReportDownloaderComponent,
      },
      {
        path: 'search',
        redirectTo: 'deliver/search',
      },
      {
        path: '**',
        component: WildCardRouteComponent,
        pathMatch: 'full',
      },
    ],
  },
];
@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
  providers: [
    {
      provide: TitleStrategy,
      useClass: TemplatePageTitleStrategy,
    },
  ],
})
export class AppRoutingModule {}
